import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { TraderLayout } from '../../layouts/TraderLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Receipt,
  Trophy,
  Download,
  Printer,
  CheckCircle,
  XCircle,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API = '/api';

export function TraderBetsReport() {
  const [races, setRaces] = useState([]);
  const [selectedRaceId, setSelectedRaceId] = useState('all');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const printRef = useRef(null);

  const fetchRaces = async () => {
    try {
      const response = await axios.get(`${API}/races`);
      // Include all races (open and settled) so trader can see settled race bets
      setRaces(response.data);
    } catch (error) {
      console.error('Failed to load races:', error);
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      let url = `${API}/reports/bets`;
      if (selectedRaceId !== 'all') {
        url += `?race_id=${selectedRaceId}`;
      }
      
      const response = await axios.get(url);
      setReport(response.data);
    } catch (error) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRaces();
  }, []);

  useEffect(() => {
    fetchReport();
  }, [selectedRaceId]);

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), 'd MMM');
    } catch {
      return dateStr;
    }
  };

  const getBetTypeColor = (betType) => {
    switch (betType) {
      case 'win': return 'bg-blue-500/20 text-blue-400';
      case 'place': return 'bg-emerald-500/20 text-emerald-400';
      case 'ew': return 'bg-yellow-500/20 text-yellow-400';
      default: return 'bg-zinc-500/20 text-zinc-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'won': return <CheckCircle className="w-4 h-4 text-emerald-400" />;
      case 'lost': return <XCircle className="w-4 h-4 text-red-400" />;
      default: return <Clock className="w-4 h-4 text-yellow-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'won': return 'text-emerald-400 bg-emerald-500/20';
      case 'lost': return 'text-red-400 bg-red-500/20';
      default: return 'text-yellow-400 bg-yellow-500/20';
    }
  };

  const exportCSV = () => {
    if (!report?.bets?.length) return;
    
    const headers = ['Race', 'Time', 'Runner #', 'Runner Name', 'Type', 'Odds', 'Stake', 'Return', 'Status'];
    const rows = report.bets.map(bet => [
      bet.race_venue,
      bet.race_time,
      bet.runner_number,
      bet.runner_name,
      bet.bet_type?.toUpperCase(),
      bet.odds_display,
      bet.stake?.toFixed(2),
      bet.potential_payout?.toFixed(2),
      bet.status?.toUpperCase()
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `my_bets_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  const printReport = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>My Bets Report</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { font-size: 18px; margin-bottom: 10px; }
            .bet-card { border: 1px solid #ddd; padding: 10px; margin-bottom: 10px; border-radius: 4px; }
            .bet-header { font-weight: bold; margin-bottom: 5px; }
            .bet-details { display: flex; justify-content: space-between; font-size: 12px; }
            .summary { margin-bottom: 20px; }
            .summary span { margin-right: 15px; }
          </style>
        </head>
        <body>
          <h1>My Bets Report - ${new Date().toLocaleDateString()}</h1>
          <div class="summary">
            <span><strong>Total:</strong> ${report?.summary?.total_bets || 0} bets</span>
            <span><strong>Stakes:</strong> €${report?.summary?.total_stakes?.toFixed(2) || '0.00'}</span>
          </div>
          ${report?.bets?.map(bet => `
            <div class="bet-card">
              <div class="bet-header">${bet.race_venue} ${bet.race_time} - #${bet.runner_number} ${bet.runner_name}</div>
              <div class="bet-details">
                <span>${bet.bet_type?.toUpperCase()} @ ${bet.odds_display}</span>
                <span>Stake: €${bet.stake?.toFixed(2)}</span>
                <span>Return: €${bet.potential_payout?.toFixed(2)}</span>
                <span>${bet.status?.toUpperCase()}</span>
              </div>
            </div>
          `).join('') || ''}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const selectedRace = races.find(r => r.id === selectedRaceId);

  return (
    <TraderLayout>
      <div className="space-y-3 pb-20" data-testid="trader-bets-report">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="font-heading text-xl font-bold text-zinc-100">Bets Report</h1>
          <div className="flex items-center gap-1">
            <Button 
              variant="outline" 
              size="sm"
              className="border-zinc-700 text-zinc-400 h-8 px-2"
              onClick={exportCSV}
              disabled={!report?.bets?.length}
            >
              <Download className="w-4 h-4" />
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              className="border-zinc-700 text-zinc-400 h-8 px-2"
              onClick={printReport}
              disabled={!report?.bets?.length}
            >
              <Printer className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Race Filter */}
        <Select value={selectedRaceId} onValueChange={setSelectedRaceId}>
          <SelectTrigger className="bg-zinc-900 border-zinc-800 text-zinc-100 h-12">
            <SelectValue>
              {selectedRaceId === 'all' ? (
                <span className="flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-blue-500" />
                  All Races
                </span>
              ) : selectedRace ? (
                <span className="flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-blue-500" />
                  <span className="font-bold">{selectedRace.venue}</span>
                  <span className="font-bold text-blue-400">{selectedRace.time}</span>
                </span>
              ) : null}
            </SelectValue>
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all">All Races</SelectItem>
            {races.map(race => (
              <SelectItem key={race.id} value={race.id}>
                <span className="flex items-center gap-2">
                  <span className="font-bold">{race.venue}</span>
                  <span className="font-bold text-blue-400">{race.time}</span>
                  <span className="text-xs text-zinc-500">{formatDate(race.date)}</span>
                  {race.status === 'settled' && (
                    <span className="text-[10px] bg-zinc-700 px-1 rounded">SETTLED</span>
                  )}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Summary */}
        {report?.summary && (
          <div className="grid grid-cols-4 gap-1.5">
            <div className="bg-zinc-900 border border-zinc-800 rounded p-2 text-center">
              <p className="text-[9px] uppercase text-zinc-500">Bets</p>
              <p className="font-mono text-sm font-bold text-zinc-100">{report.summary.total_bets}</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded p-2 text-center">
              <p className="text-[9px] uppercase text-zinc-500">Staked</p>
              <p className="font-mono text-sm font-bold text-zinc-100">€{report.summary.total_stakes?.toFixed(2)}</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded p-2 text-center">
              <p className="text-[9px] uppercase text-zinc-500">Win</p>
              <p className="font-mono text-sm font-bold text-blue-400">{report.summary.win_bets}</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded p-2 text-center">
              <p className="text-[9px] uppercase text-zinc-500">E/W</p>
              <p className="font-mono text-sm font-bold text-yellow-400">{report.summary.ew_bets}</p>
            </div>
          </div>
        )}

        {/* Bets List */}
        <div ref={printRef} className="space-y-2">
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-24 bg-zinc-900 animate-pulse rounded border border-zinc-800" />
              ))}
            </div>
          ) : !report?.bets?.length ? (
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="py-8 text-center text-zinc-500">
                <Receipt className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No bets found</p>
              </CardContent>
            </Card>
          ) : (
            report.bets.map(bet => (
              <div 
                key={bet.id}
                className="bg-zinc-900 border border-zinc-800 rounded-lg p-3 space-y-2"
              >
                {/* Race Info */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Trophy className="w-4 h-4 text-blue-500" />
                    <span className="font-heading text-base font-bold text-zinc-100">{bet.race_venue}</span>
                    <span className="font-heading text-base font-bold text-blue-400">{bet.race_time}</span>
                  </div>
                  <div className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold ${getStatusColor(bet.status)}`}>
                    {getStatusIcon(bet.status)}
                    <span className="uppercase">{bet.status}</span>
                  </div>
                </div>
                
                {/* Runner Info */}
                <div className="flex items-center gap-2">
                  <span className="font-mono text-lg font-bold text-zinc-400">#{bet.runner_number}</span>
                  <span className="text-sm font-medium text-zinc-100">{bet.runner_name}</span>
                </div>
                
                {/* Bet Details */}
                <div className="flex items-center justify-between pt-2 border-t border-zinc-800">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${getBetTypeColor(bet.bet_type)}`}>
                      {bet.bet_type === 'ew' ? 'E/W' : bet.bet_type}
                    </span>
                    <span className="font-mono text-sm text-zinc-300">@{bet.odds_display}</span>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-zinc-500">Stake</p>
                    <p className="font-mono text-sm font-bold text-zinc-100">€{bet.stake?.toFixed(2)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-zinc-500">Return</p>
                    <p className="font-mono text-sm font-bold text-emerald-400">€{bet.potential_payout?.toFixed(2)}</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </TraderLayout>
  );
}
